import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { PermissionProvider } from "./context/PermissionContext";
import { SettingsProvider } from "./context/SettingsContext";
import { ToastContainer } from "./components/common/Toast";
import { ProtectedRoute } from "./components/common/ProtectedRoute";
import { AppShell } from "./components/layout/AppShell";
import { Spinner } from "./components/common/Spinner";
import { ErrorBoundary } from "./components/common/ErrorBoundary";
import { useAuth } from "./hooks/useAuth";

// ─── Auth ─────────────────────────────────────────────────────────────────────
import Login from "./pages/auth/Login";
import LandingPage from "./pages/LandingPage";

// ─── Lazy-loaded page modules ─────────────────────────────────────────────────
// Pages are lazy-imported so each module is only bundled when first visited.
// Modules not yet implemented are commented out — uncomment as tasks complete.

// Dashboard
const Dashboard = lazy(() => import("./pages/dashboard/Dashboard"));

// Inventory
const Products = lazy(() => import("./pages/inventory/Products"));
const ProductForm = lazy(() => import("./pages/inventory/ProductForm"));
const StockMovements = lazy(() => import("./pages/inventory/StockMovements"));
const BarcodeView = lazy(() => import("./pages/inventory/BarcodeView"));

// Sales Orders
const SalesOrders = lazy(() => import("./pages/sales/SalesOrders"));
const SalesOrderForm = lazy(() => import("./pages/sales/SalesOrderForm"));

// Sales Quotations
const SalesQuotes = lazy(() => import("./pages/sales/SalesQuotes"));
const SalesQuoteForm = lazy(() => import("./pages/sales/SalesQuoteForm"));

// Sales Invoices
const SalesInvoices = lazy(() => import("./pages/sales/SalesInvoices"));
const InvoiceForm = lazy(() => import("./pages/sales/InvoiceForm"));
const ReceiptPage = lazy(() => import("./pages/sales/ReceiptPage"));

// Credit Notes
const CreditNotes = lazy(() => import("./pages/credit-notes/CreditNotes"));
const CreditNoteForm = lazy(
  () => import("./pages/credit-notes/CreditNoteForm"),
);

// Debtors (Customers)
const Customers = lazy(() => import("./pages/debtors/Customers"));
const CustomerLedger = lazy(() => import("./pages/debtors/CustomerLedger"));
const AgingReport = lazy(() => import("./pages/debtors/AgingReport"));

// Creditors (Suppliers)
const Suppliers = lazy(() => import("./pages/creditors/Suppliers"));
const SupplierLedger = lazy(() => import("./pages/creditors/SupplierLedger"));
const SupplierAgingReport = lazy(
  () => import("./pages/creditors/SupplierAgingReport"),
);

// Purchases
const Purchases = lazy(() => import("./pages/purchases/Purchases"));
const PurchaseForm = lazy(() => import("./pages/purchases/PurchaseForm"));
const PurchaseReturns = lazy(
  () => import("./pages/purchases/PurchaseReturns"),
);
const PurchaseReturnForm = lazy(
  () => import("./pages/purchases/PurchaseReturnForm"),
);

// Payments
const Payments = lazy(() => import("./pages/payments/Payments"));
const PaymentForm = lazy(() => import("./pages/payments/PaymentForm"));

// Reports
const ReportsDashboard = lazy(
  () => import("./pages/reports/ReportsDashboard"),
);

// Assets
const AssetList = lazy(() => import("./pages/assets/AssetList"));
const AssetForm = lazy(() => import("./pages/assets/AssetForm"));
const AssetDetail = lazy(() => import("./pages/assets/AssetDetail"));
const AssetCategories = lazy(() => import("./pages/assets/AssetCategories"));
const AssetReports = lazy(() => import("./pages/assets/AssetReports"));

// Expenses
const ExpenseList = lazy(() => import("./pages/expenses/ExpenseList"));
const ExpenseForm = lazy(() => import("./pages/expenses/ExpenseForm"));
const ExpenseDetail = lazy(() => import("./pages/expenses/ExpenseDetail"));
const ExpenseCategories = lazy(
  () => import("./pages/expenses/ExpenseCategories"),
);
const EmployeeList = lazy(() => import("./pages/expenses/EmployeeList"));
const EmployeeForm = lazy(() => import("./pages/expenses/EmployeeForm"));
const PayrollSummary = lazy(
  () => import("./pages/expenses/PayrollSummary"),
);
const PositionsPage = lazy(() => import("./pages/expenses/PositionsPage"));

// Settings
const ShopSettings = lazy(() => import("./pages/settings/ShopSettings"));
const GstIndiaSettings = lazy(
  () => import("./pages/settings/GstIndiaSettings"),
);
const InvoiceSettings = lazy(
  () => import("./pages/settings/InvoiceSettings"),
);
const UserManagement = lazy(
  () => import("./pages/settings/UserManagement"),
);
const AuditLog = lazy(() => import("./pages/settings/AuditLog"));
const CategoryManagement = lazy(
  () => import("./pages/settings/CategoryManagement"),
);
const PermissionManagement = lazy(
  () => import("./pages/settings/PermissionManagement"),
);
const UnitsOfMeasure = lazy(
  () => import("./pages/settings/UnitsOfMeasure"),
);
const PlatformDashboard = lazy(
  () => import("./pages/platform/PlatformDashboard"),
);
const SubscriptionsPage = lazy(
  () => import("./pages/settings/SubscriptionsPage"),
);

// Gap Closure Pages
const AccountingPeriods = lazy(
  () => import("./pages/settings/AccountingPeriods"),
);
const Warehouses = lazy(() => import("./pages/inventory/Warehouses"));
const WarehouseTransfers = lazy(
  () => import("./pages/inventory/WarehouseTransfers"),
);
const Brands = lazy(() => import("./pages/inventory/Brands"));
const GoodsReceipts = lazy(() => import("./pages/purchases/GoodsReceipts"));
const SupplierBills = lazy(() => import("./pages/creditors/SupplierBills"));
const DocumentManager = lazy(
  () => import("./pages/settings/DocumentManager"),
);
const Stocktake = lazy(() => import("./pages/inventory/Stocktake"));
const ErrorBoundaryTestPage = lazy(() => import("./pages/test/ErrorBoundaryTestPage"));

// ─── Page-level loading fallback ──────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Spinner size="lg" className="text-primary-600" />
    </div>
  );
}

// ─── Role sets (for outer shell authentication check) ────────────────────────
const ALL_ROLES = ["super_admin", "admin", "manager", "staff", "cashier"] as const;

function RootRedirect() {
  const { role } = useAuth();
  if (role === "super_admin") {
    return <Navigate to="/platform" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

// ─── App ──────────────────────────────────────────────────────────────────────
function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary level="root">
        <AuthProvider>
          <PermissionProvider>
            <SettingsProvider>
              <ToastContainer>
              <Routes>
              {/* ── Public routes ─────────────────────────────────────── */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />

              {/* ── Protected routes (require authentication) ─────────── */}
              <Route
                element={
                  <ProtectedRoute requiredRoles={[...ALL_ROLES]}>
                    <AppShell />
                  </ProtectedRoute>
                }
              >
                {/* Dashboard — all roles */}
                <Route
                  path="dashboard"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="dashboard">
                        <Dashboard />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />

                {/* ── Inventory ──────────────────────────────────────── */}
                <Route
                  path="inventory/products"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="inventory">
                        <Products />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="inventory/products/new"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="inventory">
                        <ProductForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="inventory/products/:id/edit"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="inventory">
                        <ProductForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="inventory/products/:id/stock-movements"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="inventory">
                        <StockMovements />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="inventory/products/:id/barcode"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="inventory">
                        <BarcodeView />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="inventory/warehouses"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="inventory">
                        <Warehouses />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="inventory/transfers"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="inventory">
                        <WarehouseTransfers />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="inventory/brands"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="inventory">
                        <Brands />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="inventory/stocktake"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="inventory">
                        <Stocktake />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />

                {/* ── Sales Orders ───────────────────────────────────── */}
                <Route
                  path="sales/orders"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="sales-orders">
                        <SalesOrders />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="sales/orders/new"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="sales-orders">
                        <SalesOrderForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="sales/orders/:id/edit"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="sales-orders">
                        <SalesOrderForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />

                {/* ── Sales Quotations ───────────────────────────────── */}
                <Route
                  path="sales/quotes"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="sales-orders">
                        <SalesQuotes />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="sales/quotes/new"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="sales-orders">
                        <SalesQuoteForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="sales/quotes/:id/edit"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="sales-orders">
                        <SalesQuoteForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />

                {/* ── Sales Invoices ─────────────────────────────────── */}
                <Route
                  path="sales/invoices"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="sales-invoices">
                        <SalesInvoices />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="sales/invoices/new"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="sales-invoices">
                        <InvoiceForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="sales/invoices/:id/edit"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="sales-invoices">
                        <InvoiceForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="sales/invoices/:id/receipt"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="sales-invoices">
                        <ReceiptPage />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />

                {/* ── Credit Notes ───────────────────────────────────── */}
                <Route
                  path="credit-notes"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="credit-notes">
                        <CreditNotes />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="credit-notes/new"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="credit-notes">
                        <CreditNoteForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="credit-notes/:id/edit"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="credit-notes">
                        <CreditNoteForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />

                {/* ── Debtors (Customers) ────────────────────────────── */}
                <Route
                  path="debtors/customers"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="customers">
                        <Customers />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="debtors/customers/:id/ledger"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="customers">
                        <CustomerLedger />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="debtors/aging-report"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="customers">
                        <AgingReport />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />

                {/* ── Creditors (Suppliers) ──────────────────────────── */}
                <Route
                  path="creditors/suppliers"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="suppliers">
                        <Suppliers />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="creditors/suppliers/:id/ledger"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="suppliers">
                        <SupplierLedger />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="creditors/supplier-bills"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="purchases">
                        <SupplierBills />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="creditors/aging-report"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="purchases">
                        <SupplierAgingReport />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />

                {/* ── Purchases ──────────────────────────────────────── */}
                <Route
                  path="purchases"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="purchases">
                        <Purchases />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="purchases/new"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="purchases">
                        <PurchaseForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="purchases/:id/edit"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="purchases">
                        <PurchaseForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="purchases/returns"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="purchase-returns">
                        <PurchaseReturns />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="purchases/goods-receipts"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="purchases">
                        <GoodsReceipts />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="purchases/supplier-bills"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="purchases">
                        <SupplierBills />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />

                {/* ── Purchase Returns (top-level, matches sidebar nav) ── */}
                <Route
                  path="purchase-returns"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="purchase-returns">
                        <PurchaseReturns />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="purchase-returns/new"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="purchase-returns">
                        <PurchaseReturnForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="purchase-returns/:id/edit"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="purchase-returns">
                        <PurchaseReturnForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />

                {/* ── Payments ───────────────────────────────────────── */}
                <Route
                  path="payments"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="payments">
                        <Payments />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="payments/new"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="payments">
                        <PaymentForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />

                {/* ── Reports ────────────────────────────────────────── */}
                <Route
                  path="reports"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="reports">
                        <ReportsDashboard />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="reports/ap-aging"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="reports">
                        <SupplierAgingReport />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="reports/:type"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="reports">
                        <ReportsDashboard />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />

                {/* ── Expenses ───────────────────────────────────────── */}
                <Route
                  path="expenses"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="expenses">
                        <ExpenseList />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="expenses/new"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute requiredRoles={["admin", "manager"]}>
                        <ExpenseForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="expenses/:id"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="expenses">
                        <ExpenseDetail />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="expenses/:id/edit"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute requiredRoles={["admin", "manager"]}>
                        <ExpenseForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="expenses/categories"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute requiredRoles={["admin"]}>
                        <ExpenseCategories />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="expenses/employees"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute requiredRoles={["admin"]}>
                        <EmployeeList />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="expenses/employees/new"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute requiredRoles={["admin"]}>
                        <EmployeeForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="expenses/employees/:id/edit"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute requiredRoles={["admin"]}>
                        <EmployeeForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="expenses/payroll"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute requiredRoles={["admin"]}>
                        <PayrollSummary />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="expenses/positions"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute requiredRoles={["admin"]}>
                        <PositionsPage />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />

                {/* ── Assets ─────────────────────────────────────────── */}
                <Route
                  path="assets"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute requiredRoles={["admin", "manager", "staff"]}>
                        <AssetList />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="assets/new"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute requiredRoles={["admin", "manager"]}>
                        <AssetForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="assets/:id"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute requiredRoles={["admin", "manager", "staff"]}>
                        <AssetDetail />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="assets/:id/edit"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute requiredRoles={["admin", "manager"]}>
                        <AssetForm />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="assets/categories"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute requiredRoles={["admin"]}>
                        <AssetCategories />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="assets/reports"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute requiredRoles={["admin", "manager", "staff"]}>
                        <AssetReports />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />

                {/* ── Settings ───────────────────────────────────────── */}
                <Route
                  path="settings/shop"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="settings">
                        <ShopSettings />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="settings/invoice"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="settings">
                        <InvoiceSettings />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="settings/users"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="user-management">
                        <UserManagement />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="settings/audit-log"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="audit-log">
                        <AuditLog />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="settings/permissions"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="settings">
                        <PermissionManagement />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="settings/categories"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute module="categories">
                        <CategoryManagement />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="settings/units-of-measure"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute requiredRoles={["admin", "manager"]}>
                        <UnitsOfMeasure />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="settings/subscriptions"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute requiredRoles={["admin"]}>
                        <SubscriptionsPage />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="settings/gst-india"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute requiredRoles={["admin"]}>
                        <GstIndiaSettings />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="settings/accounting-periods"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute requiredRoles={["admin"]}>
                        <AccountingPeriods />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="settings/documents"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute requiredRoles={["admin", "manager"]}>
                        <DocumentManager />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="platform"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute requiredRoles={["super_admin"]}>
                        <PlatformDashboard />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path="test/error-boundary"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ErrorBoundaryTestPage />
                    </Suspense>
                  }
                />
              </Route>

              {/* ── Catch-all ─────────────────────────────────────────── */}
              <Route path="*" element={<RootRedirect />} />
            </Routes>
          </ToastContainer>
          </SettingsProvider>
        </PermissionProvider>
      </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
